import { BasePaymentGatewayAdapter } from './BasePaymentGatewayAdapter';
import {
  PaymentData,
  PaymentToken,
  GatewayConfig,
  TokenizationError,
  GatewayInitializationError
} from '../../types/payment';

// Declare Square global types
declare global {
  interface Window {
    Square?: {
      payments: (applicationId: string, locationId: string) => Promise<SquarePayments>;
    };
  }
}

// Square SDK Types
interface SquarePayments {
  card: (options?: CardOptions) => Promise<SquareCard>;
  verifyBuyer: (token: string, details: VerificationDetails) => Promise<VerificationResult>;
}

interface SquareCard {
  attach: (selector: string) => Promise<void>;
  tokenize: () => Promise<TokenResult>;
  destroy: () => Promise<void>;
  addEventListener: (event: string, callback: (event: CardEvent) => void) => void;
  removeEventListener: (event: string, callback: (event: CardEvent) => void) => void;
}

interface CardOptions {
  style?: {
    input?: Record<string, string>;
    '.input-container'?: Record<string, string>;
    '.input-container.is-focus'?: Record<string, string>;
    '.input-container.is-error'?: Record<string, string>;
    '.message-text'?: Record<string, string>;
    '.message-text.is-error'?: Record<string, string>;
  };
}

interface CardEvent {
  detail?: {
    field?: string;
    message?: string;
  };
}

interface TokenResult {
  status: 'OK' | 'ERROR';
  token?: string;
  errors?: Array<{ code: string; message: string; field?: string }>;
  details?: {
    card?: {
      brand: string;
      expMonth: number;
      expYear: number;
      last4: string;
    };
    billing?: {
      postalCode: string;
    };
  };
}

interface VerificationDetails {
  amount: string;
  currencyCode: string;
  intent: 'CHARGE' | 'STORE' | 'CHARGE_AND_STORE';
  billingContact?: {
    givenName?: string;
    familyName?: string;
    email?: string;
    phone?: string;
    addressLines?: string[];
    city?: string;
    state?: string;
    countryCode?: string;
    postalCode?: string;
  };
}

interface VerificationResult {
  token: string;
  userChallenged: boolean;
}

// Extended GatewayConfig for Square
interface SquareGatewayConfig extends GatewayConfig {
  locationId?: string;
}

/**
 * Square payment gateway adapter using Square Web Payments SDK.
 *
 * Handles:
 * - SDK initialization with Application ID and Location ID
 * - Secure card form rendering
 * - Card tokenization with buyer verification (SCA/3DS compliant)
 * - Cleanup of SDK resources
 */
export class SquareGatewayAdapter extends BasePaymentGatewayAdapter {
  private payments: SquarePayments | null = null;
  private card: SquareCard | null = null;
  private locationId: string = '';

  constructor() {
    super('square');
  }

  /**
   * Initialize Square Web Payments SDK.
   */
  async initialize(config: SquareGatewayConfig): Promise<void> {
    try {
      this.config = config;
      this.locationId = config.locationId || '';

      if (!config.publicKey) {
        throw new GatewayInitializationError(
          'Square Application ID is required',
          'missing_application_id',
          this.provider
        );
      }

      if (!this.locationId) {
        throw new GatewayInitializationError(
          'Square Location ID is required',
          'missing_location_id',
          this.provider
        );
      }

      // Check if Square SDK is already loaded
      if (window.Square) {
        console.log('Square SDK already loaded, reusing...');
      } else {
        // Determine SDK URL based on environment
        const sdkUrl = config.environment === 'production'
          ? 'https://web.squarecdn.com/v1/square.js'
          : 'https://sandbox.web.squarecdn.com/v1/square.js';

        console.log(`Loading Square SDK from: ${sdkUrl}`);

        // Remove any existing failed script tag to force reload
        const existingScript = document.getElementById('square-js');
        if (existingScript) {
          existingScript.remove();
        }

        // Load Square Web Payments SDK
        await this.loadScript(sdkUrl, 'square-js');

        // Wait a bit for SDK to initialize
        await new Promise(resolve => setTimeout(resolve, 100));

        if (!window.Square) {
          throw new GatewayInitializationError(
            'Square SDK failed to load',
            'sdk_load_failed',
            this.provider
          );
        }
      }

      // Initialize Square payments instance
      this.payments = await window.Square.payments(
        config.publicKey,    // Application ID
        this.locationId      // Location ID
      );

      if (!this.payments) {
        throw new GatewayInitializationError(
          'Failed to initialize Square payments',
          'square_init_failed',
          this.provider
        );
      }

      this.initialized = true;
      console.log('Square gateway initialized successfully');

    } catch (error) {
      console.error('Failed to initialize Square gateway:', error);
      throw error instanceof GatewayInitializationError
        ? error
        : new GatewayInitializationError(
            `Square initialization failed: ${error}`,
            'init_error',
            this.provider
          );
    }
  }

  /**
   * Create and attach Square Card form to a container element.
   */
  async createCardElement(containerId: string): Promise<void> {
    this.requireInitialized();

    // If card already exists, destroy it first to prevent duplicates
    if (this.card) {
      try {
        await this.card.destroy();
      } catch (e) {
        console.warn('Error destroying existing card:', e);
      }
      this.card = null;
    }

    try {
      this.card = await this.payments!.card({
        style: {
          input: {
            fontSize: '16px',
            color: '#1f2937',
            backgroundColor: 'transparent',
          },
          '.input-container': {
            borderRadius: '6px',
            borderColor: '#d1d5db',
          },
          '.input-container.is-focus': {
            borderColor: '#3b82f6',
          },
          '.input-container.is-error': {
            borderColor: '#ef4444',
          },
          '.message-text': {
            color: '#6b7280',
          },
          '.message-text.is-error': {
            color: '#ef4444',
          },
        }
      });

      await this.card.attach(`#${containerId}`);

      this.card.addEventListener('errorClassAdded', (event: CardEvent) => {
        console.log('Card validation error:', event.detail);
      });

      this.card.addEventListener('errorClassRemoved', () => {
        console.log('Card validation error cleared');
      });

      console.log('Square card element attached successfully');

    } catch (error) {
      console.error('Failed to create Square card element:', error);
      throw new GatewayInitializationError(
        `Failed to create card element: ${error}`,
        'card_element_failed',
        this.provider
      );
    }
  }

  /**
   * Tokenize payment with buyer verification (SCA/3DS compliant).
   */
  async tokenizePayment(paymentData: PaymentData): Promise<PaymentToken> {
    this.requireInitialized();

    if (!this.card) {
      throw new TokenizationError(
        'Card element not created. Call createCardElement() first.',
        'no_card_element',
        this.provider
      );
    }

    try {
      // Step 1: Tokenize the card
      const tokenResult = await this.card.tokenize();

      if (tokenResult.status !== 'OK' || !tokenResult.token) {
        const errorMessage = tokenResult.errors
          ?.map(e => e.message)
          .join(', ') || 'Tokenization failed';

        throw new TokenizationError(
          errorMessage,
          tokenResult.errors?.[0]?.code || 'tokenization_failed',
          this.provider
        );
      }

      // Step 2: Build verification details for SCA/3DS
      const verificationDetails: VerificationDetails = {
        amount: paymentData.amount?.toString() || '0',
        currencyCode: paymentData.currency || 'USD',
        intent: 'CHARGE',
        billingContact: this.buildBillingContact(paymentData),
      };

      const cardNonce = tokenResult.token;
      let verificationToken: string | null = null;

      // Step 3: Attempt buyer verification (handles 3DS when required)
      try {
        const verificationResult = await this.payments!.verifyBuyer(
          tokenResult.token,
          verificationDetails
        );
        verificationToken = verificationResult.token;

        if (verificationResult.userChallenged) {
          console.log('User completed 3DS verification challenge');
        }
      } catch (verifyError) {
        // Verification may fail for non-EU cards or in sandbox - continue without it
        console.warn('Buyer verification skipped or failed:', verifyError);
      }

      const cardDetails = tokenResult.details?.card;

      console.log('Square tokenization successful:', {
        cardNonce: cardNonce.substring(0, 10) + '...',
        hasVerificationToken: !!verificationToken,
      });

      return {
        token: cardNonce,
        type: 'nonce',
        last_four_digits: cardDetails?.last4 || '****',
        card_brand: (cardDetails?.brand || 'UNKNOWN').toLowerCase(),
        exp_month: cardDetails?.expMonth,
        exp_year: cardDetails?.expYear,
        gateway_provider: this.provider,
        ...(verificationToken && { verification_token: verificationToken }),
      };

    } catch (error) {
      console.error('Square tokenization failed:', error);

      if (error instanceof TokenizationError) {
        throw error;
      }

      throw new TokenizationError(
        `Tokenization failed: ${error}`,
        'tokenization_error',
        this.provider
      );
    }
  }

  /**
   * Build billing contact object for buyer verification.
   */
  private buildBillingContact(paymentData: PaymentData): VerificationDetails['billingContact'] {
    const nameParts = (paymentData.cardholderName || '').split(' ');
    const givenName = nameParts[0] || '';
    const familyName = nameParts.slice(1).join(' ') || '';

    return {
      givenName,
      familyName,
      email: paymentData.email,
      phone: paymentData.phone,
      addressLines: paymentData.billingAddress?.street
        ? [paymentData.billingAddress.street]
        : undefined,
      city: paymentData.billingAddress?.city,
      state: paymentData.billingAddress?.state,
      countryCode: paymentData.billingAddress?.country || 'US',
      postalCode: paymentData.billingAddress?.zipCode,
    };
  }

  /**
   * Clean up Square SDK resources.
   */
  cleanup(): void {
    if (this.card) {
      try {
        this.card.destroy();
      } catch (error) {
        console.warn('Error destroying Square card:', error);
      }
      this.card = null;
    }

    this.payments = null;
    this.locationId = '';

    super.cleanup();
  }

  /**
   * Check if Square SDK is available in the browser.
   */
  isAvailable(): boolean {
    return typeof window !== 'undefined';
  }

  /**
   * Get formatted payment method info for display.
   */
  getPaymentMethodInfo(token: PaymentToken): {
    brand: string;
    last4: string;
    displayName: string;
  } {
    const brandMap: Record<string, string> = {
      'visa': 'Visa',
      'mastercard': 'Mastercard',
      'american_express': 'American Express',
      'amex': 'American Express',
      'discover': 'Discover',
      'jcb': 'JCB',
      'diners_club': 'Diners Club',
      'unknown': 'Card'
    };

    const brand = brandMap[token.card_brand.toLowerCase()] || token.card_brand;

    return {
      brand,
      last4: token.last_four_digits,
      displayName: `${brand} •••• ${token.last_four_digits}`
    };
  }
}
